import { useEffect, useState } from 'react';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { useBillingCycles, useCreateSubscriptionPlan } from '@/hooks/billing';
import { useFeatureFlags } from '@/hooks/useFeatureFlags';
import { SubscriptionPlan } from '@/types/billing';
import { PlanPricingData, SubscriptionPlanCreateData } from '@/services/billing/subscription-plans.service';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';

interface SubscriptionPlanVariantDialogProps {
  productId: string;
  basePlan: SubscriptionPlan;
  isOpen: boolean;
  onClose: () => void;
  activeCurrencies?: string[];
  defaultCurrency?: string;
}

export function SubscriptionPlanVariantDialog({
  productId,
  basePlan,
  isOpen,
  onClose,
  activeCurrencies,
  defaultCurrency = 'USD',
}: SubscriptionPlanVariantDialogProps) {
  const { t } = useTranslation();
  const { data: billingCycles = [] } = useBillingCycles(productId);
  const createMutation = useCreateSubscriptionPlan();
  const { data: featureFlagsResponse } = useFeatureFlags(productId);
  const availableFeatureFlags = featureFlagsResponse?.data || [];
  const currencies = activeCurrencies && activeCurrencies.length > 0 ? activeCurrencies : [defaultCurrency];
  const isTeamsPlan =
    (basePlan.planType ?? basePlan.plan_type ?? basePlan.type) === 'teams';

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [overridePricing, setOverridePricing] = useState(false);
  const [pricingOverrides, setPricingOverrides] = useState<Record<string, Record<string, number>>>({});
  const [overrideTrial, setOverrideTrial] = useState(false);
  const [freeTrialDays, setFreeTrialDays] = useState(0);
  const [trialRequiresCard, setTrialRequiresCard] = useState(false);
  const [overrideSeats, setOverrideSeats] = useState(false);
  const [includedSeatsOverride, setIncludedSeatsOverride] = useState('');
  const [minSeatsOverride, setMinSeatsOverride] = useState('');
  const [maxSeatsOverride, setMaxSeatsOverride] = useState('');
  const [overrideCredits, setOverrideCredits] = useState(false);
  const [includeCredits, setIncludeCredits] = useState(false);
  const [creditsPerCycle, setCreditsPerCycle] = useState(0);
  const [allowTopUp, setAllowTopUp] = useState(false);
  const [topUpPricesPerCredit, setTopUpPricesPerCredit] = useState<Record<string, number>>({});
  const [overrideFeatureFlags, setOverrideFeatureFlags] = useState(false);
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const resolveCreditSettings = (plan: SubscriptionPlan) => {
    const rawSettings =
      plan.creditSettings ?? plan.credit_settings ?? (plan as SubscriptionPlan & { 'credit-settings'?: unknown })['credit-settings'];
    if (!rawSettings || typeof rawSettings !== 'object') {
      return {
        includeCredits: false,
        creditsPerCycle: 0,
        allowTopUp: false,
        topUpPricesPerCredit: {},
      };
    }

    const settings = rawSettings as unknown as Record<string, unknown>;
    const attributes =
      settings.attributes && typeof settings.attributes === 'object'
        ? (settings.attributes as Record<string, unknown>)
        : null;
    const source = attributes ?? settings;

    return {
      includeCredits: Boolean(
        source.includeCredits ??
          source.includesCredits ??
          source.includes_credits ??
          source.include_credits ??
          source['include-credits'],
      ),
      creditsPerCycle:
        (source.creditsPerCycle as number) ??
        (source.credits_per_cycle as number) ??
        (source['credits-per-cycle'] as number) ??
        0,
      allowTopUp: Boolean(
        source.allowTopUp ??
          source.allowPurchase ??
          source.allow_purchase ??
          source['allow-top-up'],
      ),
      topUpPricesPerCredit:
        (source.topUpPricesPerCredit as Record<string, number>) ??
        (source.top_up_prices_per_credit as Record<string, number>) ??
        {},
    };
  };

  useEffect(() => {
    if (!isOpen) return;
    const baseTrialDays = basePlan.freeTrialDays ?? basePlan.trialDays ?? basePlan.trial_days ?? 0;
    const baseTrialRequiresCard =
      basePlan.trialRequiresCard ?? basePlan.trial_requires_card ?? false;
    const baseIncludedSeats = basePlan.includedSeats ?? basePlan.included_seats;
    const baseMinSeats = (basePlan as SubscriptionPlan & { minSeats?: number; min_seats?: number }).minSeats ??
      (basePlan as SubscriptionPlan & { minSeats?: number; min_seats?: number }).min_seats;
    const baseMaxSeats = (basePlan as SubscriptionPlan & { maxSeats?: number; max_seats?: number }).maxSeats ??
      (basePlan as SubscriptionPlan & { maxSeats?: number; max_seats?: number }).max_seats;
    const baseCredits = resolveCreditSettings(basePlan);
    setName(`${basePlan.name} - Variant`);
    setDescription('');
    setOverridePricing(false);
    setPricingOverrides({});
    setOverrideTrial(false);
    setFreeTrialDays(baseTrialDays);
    setTrialRequiresCard(baseTrialRequiresCard);
    setOverrideSeats(false);
    setIncludedSeatsOverride(typeof baseIncludedSeats === 'number' && baseIncludedSeats > 0 ? String(baseIncludedSeats) : '');
    setMinSeatsOverride(typeof baseMinSeats === 'number' && baseMinSeats > 0 ? String(baseMinSeats) : '');
    setMaxSeatsOverride(typeof baseMaxSeats === 'number' && baseMaxSeats > 0 ? String(baseMaxSeats) : '');
    setOverrideCredits(false);
    setIncludeCredits(baseCredits.includeCredits);
    setCreditsPerCycle(baseCredits.creditsPerCycle ?? 0);
    setAllowTopUp(baseCredits.allowTopUp);
    setTopUpPricesPerCredit(baseCredits.topUpPricesPerCredit ?? {});
    setOverrideFeatureFlags(false);
    setFeatureFlags(basePlan.featureFlags ?? {});
    setValidationErrors({});
  }, [basePlan, isOpen]);

  const handlePriceChange = (cycleId: string, currency: string, value: number) => {
    setPricingOverrides((prev) => ({
      ...prev,
      [cycleId]: {
        ...(prev[cycleId] ?? {}),
        [currency]: value,
      },
    }));
  };

  const validate = () => {
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = t('products.subscriptionPlanEditor.validation.nameRequired');
    }

    if (overridePricing) {
      let hasAnyCycle = false;
      billingCycles.forEach((cycle) => {
        const cyclePrices = pricingOverrides[cycle.id];
        const hasAnyPrice = currencies.some((currency) => (cyclePrices?.[currency] ?? 0) > 0);
        if (!hasAnyPrice) {
          return;
        }
        hasAnyCycle = true;
        currencies.forEach((currency) => {
          const price = cyclePrices?.[currency];
          if (!price || price <= 0) {
            errors[`${cycle.id}-${currency}`] = t(
              'products.subscriptionPlanEditor.validation.priceForCurrency',
              { currency }
            );
          }
        });
      });

      if (!hasAnyCycle) {
        errors.pricing = t('products.subscriptionPlanVariants.pricingEmptyError');
      }
    }

    if (overrideCredits && allowTopUp) {
      currencies.forEach((currency) => {
        const price = topUpPricesPerCredit[currency];
        if (!price || price <= 0) {
          errors[`topup-${currency}`] = t(
            'products.subscriptionPlanEditor.validation.topUpPriceForCurrency',
            { currency }
          );
        }
      });
    }

    if (overrideSeats && isTeamsPlan) {
      let hasAnySeatOverride = false;
      const parseSeatValue = (value: string, key: string) => {
        const trimmed = value.trim();
        if (!trimmed) {
          return;
        }
        hasAnySeatOverride = true;
        const parsed = Number(trimmed);
        if (!Number.isInteger(parsed) || parsed < 1) {
          errors[key] = t('products.subscriptionPlanVariants.seatsInvalidError');
        }
      };

      parseSeatValue(includedSeatsOverride, 'included-seats');
      parseSeatValue(minSeatsOverride, 'min-seats');
      parseSeatValue(maxSeatsOverride, 'max-seats');

      if (!hasAnySeatOverride) {
        errors.seats = t('products.subscriptionPlanVariants.seatsEmptyError');
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast.error(t('products.subscriptionPlanEditor.toasts.fixValidation'));
      return;
    }

    const submitData: SubscriptionPlanCreateData = {
      name: name.trim(),
      parentPlanId: basePlan.id,
    };

    if (description.trim()) {
      submitData.description = description.trim();
    }

    if (overrideTrial) {
      submitData.freeTrialDays = freeTrialDays;
      submitData.trialRequiresCard = trialRequiresCard;
    }

    if (overrideSeats && isTeamsPlan) {
      const parseSeatValue = (value: string) => {
        const trimmed = value.trim();
        if (!trimmed) return undefined;
        const parsed = Number(trimmed);
        return Number.isInteger(parsed) && parsed >= 1 ? parsed : undefined;
      };
      const includedSeats = parseSeatValue(includedSeatsOverride);
      const minSeats = parseSeatValue(minSeatsOverride);
      const maxSeats = parseSeatValue(maxSeatsOverride);

      if (includedSeats !== undefined) {
        submitData.includedSeats = includedSeats;
      }
      if (minSeats !== undefined) {
        submitData.minSeats = minSeats;
      }
      if (maxSeats !== undefined) {
        submitData.maxSeats = maxSeats;
      }
    }

    if (overrideCredits) {
      submitData.creditSettings = {
        includeCredits,
        creditsPerCycle,
        allowTopUp,
        topUpPricesPerCredit: allowTopUp ? topUpPricesPerCredit : {},
      };
    }

    if (overrideFeatureFlags) {
      submitData.featureFlags = featureFlags;
    }

    if (overridePricing) {
      const pricing: PlanPricingData[] = [];
      billingCycles.forEach((cycle) => {
        const cyclePrices = pricingOverrides[cycle.id];
        const hasAnyPrice = currencies.some((currency) => (cyclePrices?.[currency] ?? 0) > 0);
        if (!hasAnyPrice) return;
        pricing.push({
          billingCycleId: cycle.id,
          prices: cyclePrices ?? {},
          defaultCurrency,
        });
      });
      if (pricing.length > 0) {
        submitData.pricing = pricing;
      }
    }

    try {
      await createMutation.mutateAsync({
        productId,
        data: submitData,
      });
      toast.success(t('products.subscriptionPlanEditor.toasts.createSuccess'));
      onClose();
    } catch (error) {
      console.error('Error creating plan variant:', error);
      toast.error(t('products.subscriptionPlanEditor.toasts.saveError'));
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t('products.subscriptionPlanVariants.dialogTitle')}</DialogTitle>
          <DialogDescription>
            {t('products.subscriptionPlanVariants.dialogDescription', { name: basePlan.name })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="variant-name">{t('products.subscriptionPlanVariants.nameLabel')}</Label>
              <Input
                id="variant-name"
                value={name}
                onChange={(e) => {
                  setName(e.target.value);
                  if (validationErrors.name) {
                    const nextErrors = { ...validationErrors };
                    delete nextErrors.name;
                    setValidationErrors(nextErrors);
                  }
                }}
                placeholder={t('products.subscriptionPlanVariants.namePlaceholder')}
                className={validationErrors.name ? 'border-destructive' : ''}
              />
              {validationErrors.name && (
                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  {validationErrors.name}
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="variant-description">{t('products.subscriptionPlanVariants.descriptionLabel')}</Label>
              <Textarea
                id="variant-description"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t('products.subscriptionPlanVariants.descriptionPlaceholder')}
                rows={1}
                className="min-h-[38px]"
              />
            </div>
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                id="variant-pricing"
                checked={overridePricing}
                onCheckedChange={setOverridePricing}
              />
              <Label htmlFor="variant-pricing" className="text-base font-normal cursor-pointer">
                {t('products.subscriptionPlanVariants.overridePricingLabel')}
              </Label>
            </div>

            {overridePricing && (
              <div className="space-y-4">
                <p className="text-sm text-muted-foreground">
                  {t('products.subscriptionPlanVariants.overridePricingHelper')}
                </p>
                {validationErrors.pricing && (
                  <p className="text-xs text-destructive flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    {validationErrors.pricing}
                  </p>
                )}
                {billingCycles.map((cycle) => (
                  <div key={cycle.id} className="p-4 border rounded-lg space-y-3">
                    <div className="font-medium">{cycle.name}</div>
                    {currencies.map((currency) => (
                      <div key={currency} className="grid grid-cols-[120px_1fr] gap-4 items-center">
                        <Label className="text-sm">{currency}</Label>
                        <div>
                          <Input
                            type="number"
                            step="0.01"
                            value={pricingOverrides[cycle.id]?.[currency] ?? ''}
                            onChange={(e) => {
                              const nextValue = parseFloat(e.target.value);
                              handlePriceChange(cycle.id, currency, Number.isNaN(nextValue) ? 0 : nextValue);
                              if (validationErrors[`${cycle.id}-${currency}`]) {
                                const nextErrors = { ...validationErrors };
                                delete nextErrors[`${cycle.id}-${currency}`];
                                setValidationErrors(nextErrors);
                              }
                            }}
                            placeholder={t('products.subscriptionPlanEditor.pricePlaceholder')}
                            className={validationErrors[`${cycle.id}-${currency}`] ? 'border-destructive' : ''}
                          />
                          {validationErrors[`${cycle.id}-${currency}`] && (
                            <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              {validationErrors[`${cycle.id}-${currency}`]}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                id="variant-trial"
                checked={overrideTrial}
                onCheckedChange={setOverrideTrial}
              />
              <Label htmlFor="variant-trial" className="text-base font-normal cursor-pointer">
                {t('products.subscriptionPlanVariants.overrideTrialLabel')}
              </Label>
            </div>

            {overrideTrial && (
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="variant-trial-days">
                    {t('products.subscriptionPlanVariants.trialDaysLabel')}
                  </Label>
                  <Input
                    id="variant-trial-days"
                    type="number"
                    min="0"
                    value={freeTrialDays}
                    onChange={(e) => setFreeTrialDays(parseInt(e.target.value) || 0)}
                  />
                </div>
                <div className="flex items-center gap-3 pt-8">
                  <Switch
                    id="variant-trial-card"
                    checked={trialRequiresCard}
                    onCheckedChange={setTrialRequiresCard}
                  />
                  <Label htmlFor="variant-trial-card" className="text-sm font-normal cursor-pointer">
                    {t('products.subscriptionPlanVariants.trialRequiresCardLabel')}
                  </Label>
                </div>
              </div>
            )}
          </div>

          {isTeamsPlan && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <Switch
                  id="variant-seats"
                  checked={overrideSeats}
                  onCheckedChange={(checked) => {
                    setOverrideSeats(checked);
                    if (!checked) {
                      const nextErrors = { ...validationErrors };
                      delete nextErrors.seats;
                      delete nextErrors['included-seats'];
                      delete nextErrors['min-seats'];
                      delete nextErrors['max-seats'];
                      setValidationErrors(nextErrors);
                    }
                  }}
                />
                <Label htmlFor="variant-seats" className="text-base font-normal cursor-pointer">
                  {t('products.subscriptionPlanVariants.overrideSeatsLabel')}
                </Label>
              </div>

              {overrideSeats && (
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    {t('products.subscriptionPlanVariants.overrideSeatsHelper')}
                  </p>
                  {validationErrors.seats && (
                    <p className="text-xs text-destructive flex items-center gap-1">
                      <AlertCircle className="h-3 w-3" />
                      {validationErrors.seats}
                    </p>
                  )}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="variant-included-seats">
                        {t('products.subscriptionPlanEditor.includedSeatsLabel')}
                      </Label>
                      <Input
                        id="variant-included-seats"
                        type="number"
                        min="1"
                        value={includedSeatsOverride}
                        onChange={(e) => {
                          setIncludedSeatsOverride(e.target.value);
                          if (validationErrors['included-seats'] || validationErrors.seats) {
                            const nextErrors = { ...validationErrors };
                            delete nextErrors['included-seats'];
                            delete nextErrors.seats;
                            setValidationErrors(nextErrors);
                          }
                        }}
                        placeholder={t('products.subscriptionPlanEditor.includedSeatsPlaceholder')}
                        className={validationErrors['included-seats'] ? 'border-destructive' : ''}
                      />
                      {validationErrors['included-seats'] && (
                        <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors['included-seats']}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="variant-min-seats">
                        {t('products.subscriptionPlanVariants.minSeatsLabel')}
                      </Label>
                      <Input
                        id="variant-min-seats"
                        type="number"
                        min="1"
                        value={minSeatsOverride}
                        onChange={(e) => {
                          setMinSeatsOverride(e.target.value);
                          if (validationErrors['min-seats'] || validationErrors.seats) {
                            const nextErrors = { ...validationErrors };
                            delete nextErrors['min-seats'];
                            delete nextErrors.seats;
                            setValidationErrors(nextErrors);
                          }
                        }}
                        placeholder={t('products.subscriptionPlanVariants.minSeatsPlaceholder')}
                        className={validationErrors['min-seats'] ? 'border-destructive' : ''}
                      />
                      {validationErrors['min-seats'] && (
                        <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors['min-seats']}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="variant-max-seats">
                        {t('products.subscriptionPlanVariants.maxSeatsLabel')}
                      </Label>
                      <Input
                        id="variant-max-seats"
                        type="number"
                        min="1"
                        value={maxSeatsOverride}
                        onChange={(e) => {
                          setMaxSeatsOverride(e.target.value);
                          if (validationErrors['max-seats'] || validationErrors.seats) {
                            const nextErrors = { ...validationErrors };
                            delete nextErrors['max-seats'];
                            delete nextErrors.seats;
                            setValidationErrors(nextErrors);
                          }
                        }}
                        placeholder={t('products.subscriptionPlanVariants.maxSeatsPlaceholder')}
                        className={validationErrors['max-seats'] ? 'border-destructive' : ''}
                      />
                      {validationErrors['max-seats'] && (
                        <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          {validationErrors['max-seats']}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                id="variant-credits"
                checked={overrideCredits}
                onCheckedChange={setOverrideCredits}
              />
              <Label htmlFor="variant-credits" className="text-base font-normal cursor-pointer">
                {t('products.subscriptionPlanVariants.overrideCreditsLabel')}
              </Label>
            </div>

            {overrideCredits && (
              <div className="space-y-4">
                <div className="flex items-center gap-3">
                  <Switch
                    id="variant-include-credits"
                    checked={includeCredits}
                    onCheckedChange={setIncludeCredits}
                  />
                  <Label htmlFor="variant-include-credits" className="text-base font-normal cursor-pointer">
                    {t('products.subscriptionPlanVariants.includeCreditsLabel')}
                  </Label>
                </div>

                {includeCredits && (
                  <div className="space-y-3">
                    <div className="space-y-2">
                      <Label htmlFor="variant-credits-per-cycle">
                        {t('products.subscriptionPlanVariants.creditsPerCycleLabel')}
                      </Label>
                      <Input
                        id="variant-credits-per-cycle"
                        type="number"
                        min="0"
                        value={creditsPerCycle}
                        onChange={(e) => setCreditsPerCycle(parseInt(e.target.value) || 0)}
                      />
                    </div>

                    <div className="flex items-center gap-3">
                      <Switch
                        id="variant-allow-topup"
                        checked={allowTopUp}
                        onCheckedChange={setAllowTopUp}
                      />
                      <Label htmlFor="variant-allow-topup" className="text-base font-normal cursor-pointer">
                        {t('products.subscriptionPlanVariants.allowTopUpLabel')}
                      </Label>
                    </div>

                    {allowTopUp && (
                      <div className="space-y-3">
                        <div className="text-sm text-muted-foreground">
                          {t('products.subscriptionPlanVariants.topUpPriceLabel')}
                        </div>
                        {currencies.map((currency) => (
                          <div key={currency} className="grid grid-cols-[120px_1fr] gap-4 items-center">
                            <Label className="text-sm">{currency}</Label>
                            <div>
                              <Input
                                type="number"
                                step="0.01"
                                value={topUpPricesPerCredit[currency] ?? ''}
                                onChange={(e) => {
                                  const nextValue = parseFloat(e.target.value);
                                  setTopUpPricesPerCredit((prev) => ({
                                    ...prev,
                                    [currency]: Number.isNaN(nextValue) ? 0 : nextValue,
                                  }));
                                  if (validationErrors[`topup-${currency}`]) {
                                    const nextErrors = { ...validationErrors };
                                    delete nextErrors[`topup-${currency}`];
                                    setValidationErrors(nextErrors);
                                  }
                                }}
                                placeholder={t('products.subscriptionPlanEditor.pricePlaceholder')}
                                className={validationErrors[`topup-${currency}`] ? 'border-destructive' : ''}
                              />
                              {validationErrors[`topup-${currency}`] && (
                                <p className="text-xs text-destructive mt-1 flex items-center gap-1">
                                  <AlertCircle className="h-3 w-3" />
                                  {validationErrors[`topup-${currency}`]}
                                </p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                id="variant-feature-flags"
                checked={overrideFeatureFlags}
                onCheckedChange={setOverrideFeatureFlags}
              />
              <Label htmlFor="variant-feature-flags" className="text-base font-normal cursor-pointer">
                {t('products.subscriptionPlanVariants.overrideFeatureFlagsLabel')}
              </Label>
            </div>

            {overrideFeatureFlags && (
              <div className="space-y-2 rounded-lg border border-zinc-800 bg-zinc-950/60 p-4">
                <div className="text-sm text-muted-foreground">
                  {t('products.subscriptionPlanVariants.featureFlagsHelper')}
                </div>
                <div className="space-y-2">
                  {availableFeatureFlags.map((flag: { key: string; name: string }) => (
                    <label key={flag.key} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={featureFlags[flag.key] ?? false}
                        onChange={(e) =>
                          setFeatureFlags((prev) => ({ ...prev, [flag.key]: e.target.checked }))
                        }
                      />
                      <span className="text-zinc-200">{flag.name}</span>
                      <span className="ml-auto text-xs text-zinc-500">{flag.key}</span>
                    </label>
                  ))}
                  {availableFeatureFlags.length === 0 && (
                    <div className="text-sm text-muted-foreground">
                      {t('products.subscriptionPlanVariants.featureFlagsEmpty')}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            {t('products.subscriptionPlanVariants.cancelButton')}
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending
              ? t('products.subscriptionPlanVariants.savingLabel')
              : t('products.subscriptionPlanVariants.saveButton')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
